<!-- Timeline.vue -->
<template>
  <div class="flex justify-between">
    <div class="w-[235px] shrink-0 p-1">
      <Controls />
    </div>
    <div>
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <span class="text-white">Scale</span>
          <DSlider v-model="pixelsPerSecond" :min="0.1" :max="240" :step="0.1" class="w-32" />
          <span class="text-sm text-white">{{ Math.round(pixelsPerSecond) }}%</span>
        </div>
        <div class="flex items-center gap-1">
          <DButton size="small" text @click="toggleTimeUnit" v-tooltip.top="`Toggle time unit (current: ${timeUnit})`">
            <Clock size="14" />
          </DButton>
          <input
            type="number"
            v-tooltip.top="`Set timeline length in ${timeUnit}`"
            :value="displayTimeValue"
            @input="handleTimelineLengthChange"
            @blur="handleTimelineLengthBlur"
            class="w-20 rounded border border-gray-600 bg-transparent px-2 py-0.5 text-center text-white transition-colors hover:bg-gray-700 focus:border-gray-500 focus:bg-gray-700 focus:outline-none"
            :min="timeUnit === 'seconds' ? 1 : framesToSeconds(1)"
            :max="timeUnit === 'seconds' ? 36000 : secondsToFrames(36000)" />
        </div>

        <div @click="togglePanel()" class="cursor-pointer">
          <PanelTopOpen
            size="20"
            stroke-width="1.8"
            class="mt-1 mr-3 text-gray-400"
            :class="[!opened ? '-rotate-180' : '']" />
        </div>
      </div>
    </div>
  </div>
  <div class="h-full overflow-hidden text-sm select-none">
    <div class="sticky top-0 z-20 flex h-10 border-b border-gray-700 bg-gray-900">
      <div class="w-[235px] shrink-0 p-1"></div>
      <!-- ticks grow to full length of timeline -->
      <div
        ref="ruler"
        class="no-scrollbar relative flex-1 overflow-x-scroll overflow-y-hidden select-none"
        @scroll="syncX"
        @wheel="handleRulerWheel"
        @click="handleClickRuler">
        <!-- ticks & labels are absolutely‑positioned so the strip itself never scrolls vertically -->
        <div class="relative h-full" :style="{ width: `${timelineLength * pixelsPerSecond}px` }">
          <PlayHeadLine :showArrow="true" :style="{ left: `${playheadPosition}px` }" />

          <template v-for="s in visibleTicks" :key="s">
            <div
              v-if="shouldShowTick(s)"
              :style="{ left: `${s * pixelsPerSecond - 3}px` }"
              class="absolute top-0 flex h-full flex-col items-start text-white">
              <div :class="['w-px bg-[#475467]', isMajorTick(s) ? 'h-full' : 'h-4', 'translate-x-0.5']"></div>

              <span v-if="shouldShowLabel(s)" class="mt-auto mb-1 text-[10px] leading-none">
                {{ formatTickLabel(s) }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- main 2‑column scroll area -->
    <div class="flex h-full">
      <!-- ◀ left: layers -->
      <div
        class="no-scrollbar w-[235px] shrink-0 overflow-y-scroll border-r border-gray-700"
        ref="layersPane"
        @scroll="syncY">
        <template v-for="row in allTimelineRows" :key="row.id">
          <MediaSourceLabel
            v-if="row.type === 'mediaSource'"
            :style="{ height: `${rowHeight}px` }"
            :name="row.name"
            :isSelected="selectionStore.selectedObjectId === row.key"
            @click="handleClickLayer(row)" />
          <LayerLabel
            v-else-if="row.type === 'keyframeLayer'"
            :style="{ height: `${rowHeight}px` }"
            :name="getLayerObjectName(row) || 'Unknown Object'"
            :label="row.property"
            :isSelected="selectionStore.selectedObjectId === row.key"
            @click="handleClickLayer(row)"
            @delete="handleDeleteLayer(row)" />
        </template>
      </div>

      <!-- ▶ right: timeline tracks -->
      <div
        ref="timelinePane"
        class="no-scrollbar relative m-0 flex-1 overflow-scroll"
        :class="{
          'cursor-grab': !scrollMouseDown,
          'cursor-grabbing': scrollMouseDown,
        }"
        @scroll="onTimelineScroll"
        @wheel="handleRulerWheel"
        @mousedown="handleScrollMouseDown"
        @mousemove="handleScrollMouseMove"
        @mouseup="handleScrollMouseUp">
        <!-- stretchy track wrapper -->
        <div class="relative h-full" :style="{ width: `${timelineLength * pixelsPerSecond}px` }">
          <PlayHeadLine :style="{ left: `${playheadPosition}px` }" />
          <!-- rows -->
          <div
            v-for="(row, rowIdx) in allTimelineRows"
            :key="row.id"
            :style="{
              top: `${rowIdx * rowHeight}px`,
              height: `${rowHeight}px`,
            }"
            class="absolute right-0 left-0 border-b border-gray-700">
            <!-- Media Source Row -->
            <template v-if="row.type === 'mediaSource'">
              <MediaSourceRow
                :sourceUrl="row.sourceUrl"
                :startTime="row.startTime"
                :duration="row.duration"
                :mediaStart="row.mediaStart"
                :pixelsPerSecond="pixelsPerSecond"
                :objectId="row.id"
                :assetType="row.assetType"
                @update-duration="handleMediaSourceDurationUpdate"
                @update-start-time="handleMediaSourceStartTimeUpdate"
                @update-media-start="handleMediaSourceMediaStartUpdate" />
            </template>

            <!-- Keyframe Layer Row -->
            <template v-else-if="row.type === 'keyframeLayer'">
              <!-- Draw line between first and last keyframe -->
              <KeyFrameLine
                v-if="row.keyframes.length > 1"
                :style="{
                  left: `${row.keyframes[0].time * pixelsPerSecond}px`,
                  width: `${
                    (row.keyframes[row.keyframes.length - 1].time - row.keyframes[0].time) * pixelsPerSecond
                  }px`,
                }" />
              <div
                v-if="isLocked(row)"
                class="pointer-events-none absolute inset-0 rounded"
                style="
                  background-image: repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 4px,
                    rgba(255, 109, 240, 0.3) 4px,
                    rgba(255, 109, 240, 0.3) 5px
                  );
                  background-color: rgba(0, 0, 0, 0.2);
                "></div>
              <KeyFrame
                v-for="(kf, index) in row.keyframes"
                :key="kf.id || `${row.id}-${kf.time}`"
                :keyframe="kf"
                :index="index"
                :pixels-per-second="pixelsPerSecond"
                :layer-id="row.id"
                :style="{
                  left: `${getKeyframeDisplayTime(row.id, index, kf.time) * pixelsPerSecond - 7}px`,
                }"
                :locked="isLocked(row)"
                @click="isLocked(row) ? null : handleKeyframeClick(row, kf)"
                @contextmenu="isLocked(row) ? null : handleKeyframeDelete(row, kf)"
                @update-time="handleKeyframeTimeUpdate" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  Controls,
  KeyFrame,
  KeyFrameLine,
  LayerLabel,
  MediaSourceLabel,
  MediaSourceRow,
  PlayHeadLine,
} from '@/components';
import { useTimelineStore } from '@/stores/api/timeline';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { computed, onMounted, ref, watch } from 'vue';

import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { getObjectByCode } from '@/threejs/objectManager';
import { textureManager } from '@/threejs/textureManager';
import { DButton, DSlider } from '@disguise/cloud-ui';

import { useNodeManager } from '@/composables/useNodeManager';
const { findNodeById } = useNodeManager();

import { render } from '@/threejs/core';
import { Clock, PanelTopOpen } from 'lucide-vue-next';

defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
});
const timelineStore = useTimelineStore();
const timelinePlayerStore = useTimelinePlayerStore();
const cruxModuleStore = useCruxModuleStore();
const selectionStore = useSelectionStore();

/* ───────────────── state ───────────────── */
const pixelsPerSecond = ref(10);

const timeUnit = computed({
  get: () => cruxModuleStore.getSettingValue('project_settings', 'timeline.timeUnit', 'seconds'),
  set: (value) => cruxModuleStore.updateModuleData('project_settings', 'timeline.timeUnit', value),
});

const fps = computed(() => {
  return cruxModuleStore.getSettingValue('project_settings', 'timeline.fps', 24);
});

const timelineLength = computed(() => {
  return cruxModuleStore.getSettingValue('project_settings', 'timeline.duration', 600);
});

const displayTimeValue = computed(() => {
  if (timeUnit.value === 'frames') {
    return secondsToFrames(timelineLength.value);
  }
  return timelineLength.value;
});

const rowHeight = 46; // px (matches 14rem row from SVG)
const keyframeSize = 17; // px

const layersPane = ref();
const timelinePane = ref();
const ruler = ref();

const scrollMouseDown = ref(false);
const scrollMouseStartX = ref(0);
const scrollStartLeft = ref(0);
const mouseDownTimeout = ref(null);

// Track temporary keyframe positions during dragging
const tempKeyframeTimes = ref(new Map());

let isSyncingLayers = false;
let isSyncingTimeline = false;

const emit = defineEmits(['toggle-timeline-panel']);

const playheadPosition = computed(() => timelinePlayerStore.currentTime * pixelsPerSecond.value);

const getMediaSources = computed(() => {
  // Get all projectors and LEDs with inputSource === 'source'
  const projectors = cruxModuleStore
    .filterModulesByType('projector')
    .filter((module) => module.data.inputSource === 'source')
    .map((module) => ({
      id: module.code,
      key: module.code,
      type: 'mediaSource',
      objectType: 'projector',
      name: module.data.name || 'Projector',
      sourceUrl: module.data.sourceUrl,
      source: module.data.source,
      startTime: module.data.mediaStartTime || 0,
      duration: module.data.mediaDuration || 10,
      mediaStart: module.data.mediaStart || 0,
      assetType: module.data.assetType, // 'IM' or 'VI'
    }));

  const leds = cruxModuleStore
    .filterModulesByType('led_screen')
    .filter((module) => module.data.inputSource === 'source')
    .map((module) => ({
      id: module.code,
      key: module.code,
      type: 'mediaSource',
      objectType: 'led_screen',
      name: module.data.name || 'LED Screen',
      sourceUrl: module.data.sourceUrl,
      source: module.data.source,
      startTime: module.data.mediaStartTime || 0,
      duration: module.data.mediaDuration || 10,
      mediaStart: module.data.mediaStart || 0,
      assetType: module.data.assetType, // 'IM' or 'VI'
    }));

  return [...projectors, ...leds];
});

const getLayersKeyframes = computed(() => {
  return cruxModuleStore.filterModulesByType('timeline_layer').map((module) => {
    return {
      id: module.code,
      key: `${module.data.objectCode}`,
      type: 'keyframeLayer',
      property: module.data.property,
      keyframes: module.data.keyframes.sort((a, b) => a.time - b.time),
    };
  });
});

// Combine media sources and keyframe layers
const allTimelineRows = computed(() => {
  return [...getMediaSources.value, ...getLayersKeyframes.value];
});

const getLayerObjectName = (layer) => {
  const object = getObjectByCode(layer.key);
  if (object) {
    return object.name ?? '';
  }
};

const stepBackward = () => {
  timelinePlayerStore.setCurrentTime(0);
  textureManager.seekToTime(0);
};

const stepForward = () => {
  timelinePlayerStore.setCurrentTime(100);
  textureManager.seekToTime(100);
};

const play = () => {
  textureManager.playAllVideos();
};

const pause = () => {
  textureManager.pauseAllVideos();
};

const timeInMinutesAndSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  // Only show decimals if there are any (and not at whole seconds)
  if (remainingSeconds % 1 === 0) {
    return `${minutes}:${remainingSeconds.toFixed(0).padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toFixed(2).padStart(5, '0')}`;
};

const formatTickLabel = (seconds) => {
  if (timeUnit.value === 'frames') {
    return secondsToFrames(seconds).toString();
  }
  return timeInMinutesAndSeconds(seconds);
};

/* ───────── refs to sync scroll positions ───────── */
const handleScrollMouseDown = (e) => {
  scrollMouseDown.value = true;
  scrollMouseStartX.value = e.x;
  scrollStartLeft.value = timelinePane.value.scrollLeft;
};

const handleScrollMouseMove = (e) => {
  if (scrollMouseDown.value) {
    const deltaX = scrollMouseStartX.value - e.x;
    timelinePane.value.scrollLeft = scrollStartLeft.value + deltaX;
  }
};

const handleScrollMouseUp = (e) => {
  clearTimeout(mouseDownTimeout.value);
  scrollMouseDown.value = false;
  scrollMouseStartX.value = 0;
};

const handleRulerWheel = (e) => {
  const zoomFactor = 0.1; // 10% zoom change per wheel step
  const minZoom = 0.01; // Minimum zoom level
  const maxZoom = 240; // Maximum zoom level

  // Calculate new zoom level based on current value
  const newZoom = pixelsPerSecond.value * (1 + (e.deltaY > 0 ? -zoomFactor : zoomFactor));

  // Apply the new zoom, but never go below minimum or above maximum
  pixelsPerSecond.value = Math.max(minZoom, Math.min(maxZoom, newZoom));
};

const handleKeyframeClick = (layer, kf) => {
  console.log(kf);
};

const secondsToFrames = (seconds) => {
  return Math.round(seconds * fps.value);
};

const framesToSeconds = (frames) => {
  return frames / fps.value;
};

const toggleTimeUnit = () => {
  timeUnit.value = timeUnit.value === 'seconds' ? 'frames' : 'seconds';
};

const handleTimelineLengthChange = (e) => {
  let value = parseInt(e.target.value, 10);

  if (isNaN(value)) return;

  // Convert frames to seconds if needed
  if (timeUnit.value === 'frames') {
    value = framesToSeconds(value);
  }

  if (value >= 1 && value <= 36000) {
    cruxModuleStore.updateModuleData('project_settings', 'timeline.duration', value);
  }
};

const handleTimelineLengthBlur = (e) => {
  // Ensure valid value on blur
  let value = parseInt(e.target.value, 10);

  // Convert frames to seconds if needed
  if (timeUnit.value === 'frames') {
    value = framesToSeconds(value);
  }

  if (isNaN(value) || value < 1) {
    const minValue = 1;
    e.target.value = timeUnit.value === 'frames' ? secondsToFrames(minValue) : minValue;
    cruxModuleStore.updateModuleData('project_settings', 'timeline.duration', minValue);
  } else if (value > 36000) {
    const maxValue = 36000;
    e.target.value = timeUnit.value === 'frames' ? secondsToFrames(maxValue) : maxValue;
    cruxModuleStore.updateModuleData('project_settings', 'timeline.duration', maxValue);
  }
};

const handleKeyframeDelete = (layer, kf) => {
  console.log('Deleting keyframe:', layer, kf);
  cruxModuleStore.deleteKeyframeAtTime(layer.key, kf.time, layer.property);
  render();
};

const getKeyframeDisplayTime = (layerId, index, actualTime) => {
  const key = `${layerId}-${index}`;
  return tempKeyframeTimes.value.get(key) ?? actualTime;
};

const handleKeyframeTimeUpdate = ({ layerId, newTime, index, isDragging }) => {
  const key = `${layerId}-${index}`;

  if (isDragging) {
    // During drag, just update the temporary position
    tempKeyframeTimes.value.set(key, newTime);
  } else {
    // On drag end, commit to store and clear temporary position
    newTime = Math.round(newTime);

    cruxModuleStore.updateKeyframeTime(layerId, index, newTime);
    tempKeyframeTimes.value.delete(key);
    render();
  }
};
const handleClickLayer = (layer) => {
  selectionStore.setSelectedObject(layer.key);
};

const handleDeleteLayer = (layer) => {
  cruxModuleStore.destroy(layer.id);
  render();
};

const handleMediaSourceDurationUpdate = ({ objectId, duration, isDragging }) => {
  if (!isDragging) {
    // Find the module and update its media duration
    const module = cruxModuleStore.cruxModules.find((m) => m.code === objectId);
    if (module) {
      cruxModuleStore.updateModuleData(module.type, 'mediaDuration', duration);
      render();
    }
  }
};

const handleMediaSourceStartTimeUpdate = ({ objectId, startTime, duration, isDragging }) => {
  if (!isDragging) {
    // Find the module and update both start time and duration
    const module = cruxModuleStore.cruxModules.find((m) => m.code === objectId);
    if (module) {
      cruxModuleStore.update(module.code, {
        ...module,
        data: {
          ...module.data,
          mediaStartTime: startTime,
          mediaDuration: duration,
        },
      });
      // Update video/image position based on timeline
      updateMediaPosition(objectId);
      render();
    }
  }
};

const handleMediaSourceMediaStartUpdate = ({ objectId, mediaStart, isDragging }) => {
  if (!isDragging) {
    const module = cruxModuleStore.cruxModules.find((m) => m.code === objectId);
    if (module) {
      cruxModuleStore.update(module.code, {
        ...module,
        data: {
          ...module.data,
          mediaStart: mediaStart,
        },
      });
      updateMediaPosition(objectId);
      render();
    }
  }
};

const updateMediaPosition = (objectId) => {
  const module = cruxModuleStore.cruxModules.find((m) => m.code === objectId);
  if (!module) return;

  const object = getObjectByCode(objectId);
  if (!object) return;

  const currentTime = timelinePlayerStore.currentTime;
  const mediaStartTime = module.data.mediaStartTime || 0;
  const mediaDuration = module.data.mediaDuration || 10;
  const mediaStart = module.data.mediaStart || 0;
  const mediaEndTime = mediaStartTime + mediaDuration;

  // Check if current time is within the media block range
  if (currentTime >= mediaStartTime && currentTime <= mediaEndTime) {
    // Calculate the time offset within the media
    const timeIntoBlock = currentTime - mediaStartTime;
    const mediaTime = mediaStart + timeIntoBlock;

    // Seek video to the correct position
    if (module.data.sourceUrl) {
      textureManager.seekToTime(mediaTime, module.data.sourceUrl);
    }
  }
};

const syncX = (e) => {
  const x = e.target.scrollLeft;
  timelinePane.value.scrollLeft = x;
};

const syncY = (e) => {
  if (isSyncingLayers) {
    isSyncingLayers = false;
    return;
  }
  isSyncingTimeline = true;
  timelinePane.value.scrollTop = e.target.scrollTop;
};

/* scroll handler for the big timeline area */
const onTimelineScroll = () => {
  if (isSyncingTimeline) {
    isSyncingTimeline = false;
    return;
  }
  const { scrollLeft, scrollTop, clientWidth } = timelinePane.value;

  // Update viewport calculations
  viewportStart.value = scrollLeft;
  viewportEnd.value = scrollLeft + clientWidth;

  // horizontal sync with ruler
  ruler.value.scrollLeft = scrollLeft;
  // vertical sync with layer list
  isSyncingLayers = true;
  layersPane.value.scrollTop = scrollTop;
};

const tickConfig = computed(() => {
  const pps = pixelsPerSecond.value;
  const frameInterval = 1 / fps.value; // seconds per frame

  // Target: maintain roughly 50-100 pixels between major ticks for optimal readability
  const targetPixelsBetweenMajorTicks = 80;

  // Calculate ideal interval in seconds based on zoom
  const idealInterval = targetPixelsBetweenMajorTicks / pps;

  // Standard intervals: 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 30, 60, 120, 300, 600
  const standardIntervals = [
    frameInterval, // 1 frame
    frameInterval * 2, // 2 frames
    frameInterval * 5, // 5 frames
    frameInterval * 10, // 10 frames
    0.1,
    0.2,
    0.5,
    1,
    2,
    5,
    10,
    20,
    30,
    60,
    120,
    300,
    600,
  ];

  // Find the closest standard interval
  let majorTickInterval = standardIntervals.reduce((prev, curr) => {
    return Math.abs(curr - idealInterval) < Math.abs(prev - idealInterval) ? curr : prev;
  });

  // Minor ticks are 1/5 of major ticks (or frames at high zoom)
  let tickInterval = majorTickInterval / 5;

  // At very high zoom, use frame intervals
  if (majorTickInterval < 1) {
    tickInterval = frameInterval;
    majorTickInterval = frameInterval * 5;
  }

  return {
    tickInterval,
    majorTickInterval,
    showLabel: (s) => {
      if (majorTickInterval < 1) {
        // For frame-level, show every major tick
        const frame = Math.round(s / frameInterval);
        return Math.abs(s - frame * frameInterval) < 0.0001 && frame % 5 === 0;
      }
      // For time-based, check if it aligns with major interval
      return Math.abs(s % majorTickInterval) < 0.0001;
    },
  };
});

const handleClickRuler = (e) => {
  const pointerX = (timelinePane.value.scrollLeft + e.x - 235) / pixelsPerSecond.value;

  const time = Math.max(0, Math.round(pointerX));

  if (time !== timelinePlayerStore.currentTime) {
    timelinePlayerStore.setCurrentTime(time);
    textureManager.seekToTime(time);
    render();
  }
};

const shouldShowTick = (s) => {
  const interval = tickConfig.value.tickInterval;
  if (interval < 1) {
    // For frame intervals, check if this is aligned to a frame
    return Math.abs(s / interval - Math.round(s / interval)) < 0.0001;
  }
  return s % interval === 0;
};

const isMajorTick = (s) => {
  const interval = tickConfig.value.majorTickInterval;
  if (interval < 1) {
    // For frame intervals, check if this is aligned to a major frame
    return Math.abs(s / interval - Math.round(s / interval)) < 0.0001;
  }
  return s % interval === 0;
};

const shouldShowLabel = (s) => tickConfig.value.showLabel(s);

// Add new refs for viewport calculations
const viewportStart = ref(0);
const viewportEnd = ref(0);

// Calculate visible ticks based on viewport
const visibleTicks = computed(() => {
  const startSecond = Math.floor(viewportStart.value / pixelsPerSecond.value);
  const endSecond = Math.ceil(viewportEnd.value / pixelsPerSecond.value);
  const tickInterval = tickConfig.value.tickInterval;

  // Add some padding to prevent pop-in during scroll
  const padding = 10; // seconds of padding on each side
  const start = Math.max(0, startSecond - padding);
  const end = Math.min(timelineLength.value, endSecond + padding);

  // Generate ticks at the calculated interval
  const ticks = [];
  let current = Math.floor(start / tickInterval) * tickInterval;
  while (current <= end) {
    ticks.push(Number(current.toFixed(6))); // Round to avoid floating point errors
    current += tickInterval;
  }
  return ticks;
});

// Auto-scroll timeline to keep playhead visible
const autoScrollToPlayhead = () => {
  if (!timelinePane.value || !timelinePlayerStore.isPlaying) return;

  const playheadX = playheadPosition.value;
  const scrollLeft = timelinePane.value.scrollLeft;
  const clientWidth = timelinePane.value.clientWidth;
  const scrollRight = scrollLeft + clientWidth;

  // Add padding so playhead doesn't go right to the edge
  const padding = 50;

  // If playhead is going off the right edge, scroll right
  if (playheadX > scrollRight - padding) {
    timelinePane.value.scrollLeft = playheadX - clientWidth + padding;
  }
  // If playhead is going off the left edge, scroll left
  else if (playheadX < scrollLeft + padding) {
    timelinePane.value.scrollLeft = playheadX - padding;
  }
};

const isLocked = (layer) => {
  const foundModule = findNodeById(layer.key);

  return foundModule?.data?.lock === true;
};

const togglePanel = () => {
  emit('toggle-timeline-panel');
};

// Watch currentTime to auto-scroll during playback
watch(
  () => timelinePlayerStore.currentTime,
  () => {
    if (timelinePlayerStore.isPlaying) {
      autoScrollToPlayhead();
    }
    // Update all media sources to sync video/image position
    updateAllMediaPositions();
  }
);

const updateAllMediaPositions = () => {
  getMediaSources.value.forEach((mediaSource) => {
    updateMediaPosition(mediaSource.id);
  });
};

// Initialize viewport on mount
onMounted(() => {
  if (timelinePane.value) {
    const { scrollLeft, clientWidth } = timelinePane.value;
    viewportStart.value = scrollLeft;
    viewportEnd.value = scrollLeft + clientWidth;
  }
});
</script>
